import React, { useState, useRef } from 'react';
import QRPay from './QRPay';
import axios from 'axios';

// Define TypeScript interfaces for props and state
interface PaymentPageProps {
  courseId: string | number;
  courseName: string;
  discountedPrice: number;
  studentId: string | number;
  onSuccess?: () => void;
  onCancel?: () => void;
  language?: 'en' | 'hi';
  toggleLanguage?: () => void;
}

interface ValidationErrors {
  image?: string | null;
  reference?: string | null;
}

interface Translations {
  [key: string]: {
    title: string;
    subtitle: string;
    paymentSteps: string;
    step1: string;
    step2: string;
    step3: string;
    step4: string;
    submitButton: string;
    processingButton: string;
    uploadScreenshot: string;
    referenceLabel: string;
    referenceHelper: string;
    cancelButton: string;
    proceedButton: string;
    successMessage: string;
    qrNote: string;
    amountLabel: string;
    uploadBtnText: string;
    changeImage: string;
    requiredField: string;
    paymentError: string;
  };
}

const API_URL = process.env.REACT_APP_API_URL;

const PaymentPage: React.FC<PaymentPageProps> = ({ 
  courseId, 
  courseName,
  discountedPrice, 
  studentId, 
  onSuccess, 
  onCancel,
  language = 'en',
  toggleLanguage
}) => {
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const translations: Translations = {
    en: {
      title: "Complete Your Payment",
      subtitle: `Pay ₹${discountedPrice} to enroll in ${courseName}`,
      paymentSteps: "Payment Steps",
      step1: "Scan the QR code using any UPI app",
      step2: "Pay the amount shown",
      step3: "Upload the screenshot of payment",
      step4: "Enter the reference number/UTR",
      submitButton: "Submit Payment Details",
      processingButton: "Processing...",
      uploadScreenshot: "Upload Screenshot",
      referenceLabel: "Payment Reference Number / UTR",
      referenceHelper: "Enter the reference number from your payment",
      cancelButton: "Cancel",
      proceedButton: "Proceed to Payment",
      successMessage: "Payment verification submitted! We'll confirm and enroll you soon.",
      qrNote: "UPI ID: 9973358663@ptsbi",
      amountLabel: "Amount",
      uploadBtnText: "Choose File",
      changeImage: "Change Image",
      requiredField: "This field is required",
      paymentError: "Error submitting payment. Please try again."
    },
    hi: {
      title: "अपना भुगतान पूरा करें",
      subtitle: `${courseName} में दाखिला लेने के लिए ₹${discountedPrice} का भुगतान करें`,
      paymentSteps: "भुगतान प्रक्रिया",
      step1: "किसी भी UPI ऐप का उपयोग करके QR कोड स्कैन करें",
      step2: "दिखाई गई राशि का भुगतान करें",
      step3: "भुगतान का स्क्रीनशॉट अपलोड करें",
      step4: "संदर्भ संख्या/UTR दर्ज करें",
      submitButton: "भुगतान विवरण जमा करें",
      processingButton: "प्रक्रिया जारी है...",
      uploadScreenshot: "स्क्रीनशॉट अपलोड करें",
      referenceLabel: "भुगतान संदर्भ संख्या / UTR",
      referenceHelper: "अपने भुगतान से संदर्भ संख्या दर्ज करें",
      cancelButton: "रद्द करें",
      proceedButton: "भुगतान के लिए आगे बढ़ें",
      successMessage: "भुगतान सत्यापन जमा किया गया! हम जल्द ही पुष्टि करेंगे और आपको नामांकित करेंगे।",
      qrNote: "UPI आईडी: 9973358663@ptsbi",
      amountLabel: "राशि",
      uploadBtnText: "फाइल चुनें",
      changeImage: "छवि बदलें",
      requiredField: "यह फ़ील्ड आवश्यक है",
      paymentError: "भुगतान जमा करने में त्रुटि। कृपया पुन: प्रयास करें।"
    }
  };

  const t = translations[language as keyof typeof translations];
  
  const upiId = "9973358663@ptsbi";
  const upiPaymentText = `UPI: ${upiId}\nAmount: ₹${discountedPrice}\nPurpose: Course Enrollment`;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(file);
        setPreviewUrl(reader.result as string);
        setValidationErrors({...validationErrors, image: null});
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    if (!uploadedImage) {
      errors.image = t.requiredField;
    }
    
    if (!referenceNumber.trim()) {
      errors.reference = t.requiredField;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setApiError(null);
    
    // Create form data for file upload
    const formData = new FormData();
    formData.append('student', studentId.toString());
    formData.append('course', courseId.toString());
    formData.append('amount', discountedPrice.toString());
    formData.append('reference_number', referenceNumber);
    
    if (uploadedImage) {
      formData.append('payment_screenshot', uploadedImage);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/courses/payments/`,
        formData,
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.status === 201) {
        setPaymentStatus('success');
        
        // Wait for 2 seconds to show success message before calling onSuccess
        setTimeout(() => {
          onSuccess && onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      setApiError(t.paymentError);
      setPaymentStatus('failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReferenceNumber(e.target.value);
    if (e.target.value.trim()) {
      setValidationErrors({...validationErrors, reference: null});
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-full overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl p-6 relative">
          <button 
            onClick={onCancel}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-white">{t.title}</h2>
          <p className="text-white text-opacity-90 mt-1">{t.subtitle}</p>
          
          {toggleLanguage && (
            <button 
              onClick={toggleLanguage}
              className="absolute top-4 right-12 bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium py-1 px-3 rounded-lg text-sm transition duration-300 mr-8"
            >
              {language === 'en' ? 'हिंदी में देखें' : 'View in English'}
            </button>
          )}
        </div>
        
        <div className="p-6">
          {paymentStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{language === 'en' ? 'Thank You!' : 'धन्यवाद!'}</h3>
              <p className="text-gray-600">{t.successMessage}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* QR Code Section */}
              <div className="flex flex-col items-center justify-start">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 w-full">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.paymentSteps}</h3>
                  <ol className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 font-medium text-sm">1</span>
                      <span>{t.step1}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 font-medium text-sm">2</span>
                      <span>{t.step2}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 font-medium text-sm">3</span>
                      <span>{t.step3}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 font-medium text-sm">4</span>
                      <span>{t.step4}</span>
                    </li>
                  </ol>
                  
                  {/* QR Pay Component */}
                  <QRPay 
                    upiId={upiId} 
                    price={discountedPrice} 
                    name={courseName} 
                    description={`Payment for ${courseName}`} 
                  />
                </div>
              </div>
              
              {/* Form Section */}
              <div>
                <div className="space-y-6">
                  {/* API Error Display */}
                  {apiError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {apiError}
                    </div>
                  )}
                  
                  {/* Upload Screenshot */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.uploadScreenshot}</label>
                    <div className="mt-1 flex flex-col items-center justify-center">
                      {previewUrl ? (
                        <div className="relative w-full">
                          <img 
                            src={previewUrl} 
                            alt="Payment Screenshot" 
                            className="mx-auto max-h-48 rounded-lg border border-gray-300 object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {t.changeImage}
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="mt-2 text-sm text-gray-500">{t.uploadBtnText}</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      {validationErrors.image && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.image}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Reference Number */}
                  <div>
                    <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
                      {t.referenceLabel}
                    </label>
                    <input
                      type="text"
                      id="reference"
                      value={referenceNumber}
                      onChange={handleReferenceChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. UTR123456789"
                    />
                    <p className="mt-1 text-xs text-gray-500">{t.referenceHelper}</p>
                    {validationErrors.reference && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.reference}</p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium flex-1"
                    >
                      {t.cancelButton}
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium flex-1 flex items-center justify-center
                        ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-blue-600 hover:to-purple-700'}`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t.processingButton}
                        </>
                      ) : t.submitButton}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;